import { styled } from "goober";
import {
  type Ref,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useRef,
} from "react";
import { Button } from "../Button";
import { SpeechRecorder } from "../SpeechRecorder";
import { ComposerControlGroup } from "./chatStyles";

const Waveform = styled("canvas")`
  flex: 1;
  min-width: 0;
  height: 24px;
  margin: 0 12px;
  color: var(--text-primary);
  opacity: 0.4;
`;

export type ComposerVoiceRecorderHandle = { cancel: () => void };

type Props = {
  ref: Ref<ComposerVoiceRecorderHandle>;
  onStream: (text: string) => void;
  onCancel: (text: string) => void;
  onSend: (text: string) => void;
  onClose: () => void;
};

export function ComposerVoiceRecorder({
  ref,
  onStream,
  onCancel,
  onSend,
  onClose,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const recorderRef = useRef<SpeechRecorder | null>(null);
  const stream = useEffectEvent(onStream);
  const cancel = useEffectEvent(onCancel);
  const send = useEffectEvent(onSend);
  const close = useEffectEvent(onClose);
  useImperativeHandle(ref, () => ({
    cancel: () => recorderRef.current?.cancel(),
  }));

  useEffect(() => {
    let transcript = "";
    const recorder = new SpeechRecorder({
      canvasRef,
      onStream: (text) => stream(text),
      onTranscript: (text) => {
        transcript = text;
      },
      onCancel: () => cancel(transcript),
      onSend: (text) => send(text),
    });
    recorderRef.current = recorder;
    try {
      recorder.start();
    } catch {
      cancel("");
      return;
    }
    return () => {
      recorder.dispose();
      close();
    };
  }, []);

  return (
    <>
      <Waveform ref={canvasRef} />
      <ComposerControlGroup>
        <Button
          type="button"
          icon="x"
          variant="secondary"
          aria-label="Stop voice mode"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => recorderRef.current?.cancel()}
        />
        <Button
          type="button"
          icon="arrow-up"
          aria-label="Send"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => recorderRef.current?.send()}
        />
      </ComposerControlGroup>
    </>
  );
}
